import React, { useEffect, useState, useRef } from "react";
import "./progressTrackerCategory.scss";
import {
  getCategoryTrackingOverallData,
  getAllCategoryTrackingFilters,
} from "../../queries/apiQueries";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { numberFormatter, MANAGER } from "../../utils";
import { SelectPicker, CheckPicker, Checkbox } from "rsuite";
import MainHeader from "../../components/Layouts/MainHeader";
import MainContent from "../../components/Layouts/MainContent";
import { Loader } from "../../components/common/Loader";

interface ModuleData {
  completedStepCount: number;
  modulestep: number;
  module: string;
  manager: string;
  l3: string;
  opportunity: number;
  actualized: number;
}

interface ManagerDataItem {
  manager: string;
  moduleData: ModuleData;
}

interface ApiResponseCategory {
  l3: string;
  managerData: ManagerDataItem[];
}

type ApiResponse = ApiResponseCategory[];

interface TableRow {
  id: string;
  category: string;
  owner: string;
  targetBenefits: string;
  capturedBenefits: string;
  status: string;
  categoryBaseline: number;
  categoryStrategy: number;
  vendorEngagement: number;
  implementation: number;
  valueCapture: number;
  progress: number;
  squareStatus: "green" | "orange" | "grey";
}

interface ProgressCircleProps {
  progress: number;
}

interface ProgressSquareProps {
  color: "green" | "orange" | "grey";
}

const ProgressCircle: React.FC<ProgressCircleProps> = ({ progress }) => {
  if (progress <= 0) return <div className="progress-circle empty"></div>;
  if (progress <= 0.25) return <div className="progress-circle quarter"></div>;
  if (progress <= 0.5) return <div className="progress-circle half"></div>;
  if (progress <= 0.75)
    return <div className="progress-circle threeQuarters"></div>;
  return <div className="progress-circle full"></div>;
};

const ProgressSquare: React.FC<ProgressSquareProps> = ({ color }) => (
  <div className={`square ${color}`}></div>
);

const ProgressTrackerCategory: React.FC = (props: any) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  var isCleared = false;
  // const allFilters = useSelector((state: any) => state.CommonReducer.allFilters);
  const managerFilterValue = useSelector(
    (state: any) => state.CommonReducer.manager
  );
  const [filterOptions, updateFilterOptions] = useState<any>({
    l1: new Array<{ l1: string }>(),
    l2: new Array<{ l2: string }>(),
    l3: new Array<{ l3: string }>(),
    manager: new Array<{ manager: string }>(),
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<any>({
    manager: managerFilterValue,
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isRightVisible, setIsRightVisible] = useState(false);
  const [tableTotalData, setTableTotalData] = useState({
    totalOpp: 0,
    totalBen: 0,
  });
  const filtersMount: any = useRef(false);
  const footerStyles = {
    padding: "10px 2px",
    borderTop: "1px solid #e5e5e5",
  };

  useEffect(() => {
    fetchData();

    getFilters();
  }, [dispatch]);

  useEffect(() => {
    props?.onManagerChange(selectedFilters.manager);
  }, [selectedFilters]);

  const fetchData = async (clearFilter: any = null) => {
    setIsLoading(true);
    try {
      let payload = {
        manager: selectedFilters.manager ? selectedFilters.manager : [],
        l1: selectedFilters.l1 ? selectedFilters.l1 : [],
        l2: selectedFilters.l2 ? selectedFilters.l2 : [],
        l3: selectedFilters.l3 ? selectedFilters.l3 : [],
      };

      if (clearFilter == "manager") {
        payload.manager = [];
        payload.l1 = [];
        payload.l2 = [];
        payload.l3 = [];
      } else if (clearFilter == "l1") {
        payload.l1 = [];
        payload.l2 = [];
        payload.l3 = [];
      } else if (clearFilter == "l2") {
        payload.l2 = [];
        payload.l3 = [];
      } else if (clearFilter == "l3") {
        payload.l3 = [];
      }
      const res = await dispatch(getCategoryTrackingOverallData(payload));
      const apiResponse = sortData(res as ApiResponse);
      const transformedData: TableRow[] = transformApiData(apiResponse);
      setTableData(transformedData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setLoading(false);
    }
    setIsLoading(false);
  };

  const sortData = (dataArray:any) => {
   function getSortKeys(item:any) {
    const steps = item.managerData[0].moduleData.moduleStepData;
    // Find the entry with the maximum modulestep value and its completed count
    let maxStepEntry = steps.reduce((acc:any, step:any) => {
      if (
        step.completedStepCount > 0 && 
        (step.modulestep > acc.modulestep ||
         (step.modulestep === acc.modulestep && step.completedStepCount > acc.completedStepCount))
      ) {
        return step;
      }
      return acc;
    }, { modulestep: -Infinity, completedStepCount: -Infinity });
    // If no step has completion, fallback on modulestep=0, completedStepCount=0
    if (maxStepEntry.modulestep === -Infinity) maxStepEntry = { modulestep: 0, completedStepCount: 0 };
    // Actualized amount for tie-breaker
    const actualized = item.managerData[0].moduleData.actualized || 0;
    return [maxStepEntry.modulestep, maxStepEntry.completedStepCount, actualized];
  }

  return dataArray.sort((a:any, b:any) => {
    const [aStep, aCount, aActual] = getSortKeys(a);
    const [bStep, bCount, bActual] = getSortKeys(b);

    // Descending order: bigger modulestep first, then by count, then by actualized
    if (bStep !== aStep) return bStep - aStep;
    if (bCount !== aCount) return bCount - aCount;
    return bActual - aActual;
  });
  };

  const transformApiData = (apiResponse: ApiResponse): TableRow[] => {
    const transformedData: TableRow[] = [];

    apiResponse.forEach((categoryObj: ApiResponseCategory) => {
      const categoryName = categoryObj.l3;
      const opportunity: any =
        categoryObj?.managerData[0]?.moduleData?.opportunity;
      const actualized: any =
        categoryObj?.managerData[0]?.moduleData?.actualized;

      const managersMap = new Map<string, any[]>();
      categoryObj.managerData.forEach((managerItem: ManagerDataItem) => {
        const managerName = managerItem.manager;
        const moduleData = managerItem.moduleData;
        if (!managersMap.has(managerName)) {
          managersMap.set(managerName, []);
        }
        managersMap.get(managerName)!.push(moduleData);
      });

      managersMap.forEach((moduleDataArray: any[], managerName: string) => {
        const moduleSteps: Record<number, number> = {
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0,
        };

        moduleDataArray.forEach((moduleData: any) => {
          const moduleStepData = moduleData.moduleStepData;
          if (moduleStepData && moduleStepData.length > 0) {
            moduleStepData.forEach((stepData: any) => {
              const step = stepData.modulestep;
              if (step in moduleSteps) {
                moduleSteps[step] += stepData.completedStepCount;
              }
            });
          }
        });

        const sum = Object.values(moduleSteps).reduce((a, b) => a + b, 0);
        const maxPossible = 4 * 5;
        const progress = sum / maxPossible;

        let squareStatus: "green" | "orange" | "grey";
        if (sum === 0) {
          squareStatus = "grey";
        } else if (sum < 19) {
          squareStatus = "orange";
        } else {
          squareStatus = "green";
        }

        const row: TableRow = {
          id: `${categoryName}-${managerName}`,
          category: categoryName,
          owner: managerName,
          targetBenefits: opportunity,
          capturedBenefits: actualized ? actualized : 0,
          status: progress > 0.5 ? "good" : "bad",
          categoryBaseline: moduleSteps[1],
          categoryStrategy: moduleSteps[2],
          vendorEngagement: moduleSteps[3],
          implementation: moduleSteps[4],
          valueCapture: moduleSteps[5],
          progress: progress,
          squareStatus: squareStatus,
        };

        transformedData.push(row);
      });
    });

    setTableTotalData({
      totalOpp: transformedData.reduce(
        (sum, cur) => sum + Number(cur.targetBenefits),
        0
      ),
      totalBen: transformedData.reduce(
        (sum, cur) => sum + Number(cur.capturedBenefits),
        0
      ),
    });

    return transformedData;
  };

  /* Filters api call */
  const getFilters = async (
    payload: any = {},
    currentFilter: any = "all",
    fo: any = null
  ) => {
    // setFiltersLoading(true);
    let res = await dispatch(getAllCategoryTrackingFilters(payload));
    if (fo) {
      if (currentFilter === "manager") {
        updateFilterOptions({
          ...fo,
          l1: [...res.l1],
          l2: [...res.l2],
          l3: [...res.l3],
        });
      } else if (currentFilter === "l1") {
        updateFilterOptions({
          ...fo,
          l2: [...res.l2],
          l3: [...res.l3],
        });
      } else if (currentFilter === "l2") {
        updateFilterOptions({
          ...fo,
          l3: [...res.l3],
        });
      } else if (currentFilter === "l3") {
        updateFilterOptions({ ...fo });
      } else {
        updateFilterOptions({
          l1: res.l1,
          l2: res.l2,
          l3: res.l3,
        });
      }
    } else {
      if (currentFilter === "manager") {
        updateFilterOptions({
          ...filterOptions,
          l1: [...res.l1],
          l2: [...res.l2],
          l3: [...res.l3],
        });
      } else if (currentFilter === "l1") {
        updateFilterOptions({
          ...filterOptions,
          l2: [...res.l2],
          l3: [...res.l3],
        });
      } else if (currentFilter === "l2") {
        updateFilterOptions({
          ...filterOptions,
          l3: [...res.l3],
        });
      } else if (currentFilter === "l3") {
        updateFilterOptions({ ...filterOptions });
      } else {
        updateFilterOptions({
          manager: res.manager,
          l1: res.l1,
          l2: res.l2,
          l3: res.l3,
        });
      }
    }

    setLoading(false);
    setFiltersLoading(false);
    filtersMount.current = false;
    return res;
  };

  // const handleFilterUpdate = (value: any, filter: string) => {
  //   filtersMount.current = true;
  //   let x: string = filter;
  //   if(x=='manager'){
  //     setSelectedFilters({ ...selectedFilters, l3: [] , [x]: [value] });
  //     selectedFilters[x] = [value];
  //   } else{
  //     setSelectedFilters({ ...selectedFilters, [x]: [value] });
  //     selectedFilters[x] = [value];
  //   }
  // };
  const handleFilterUpdate = (value: any, filter: string) => {
    if (isCleared) {
      isCleared = false;
      return;
    }
    filtersMount.current = true;
    let x: string = filter;
    if (value === "all") {
      if (filterOptions?.[x].length === selectedFilters?.[x].length) {
        setSelectedFilters({ ...selectedFilters, [x]: [] });
      } else {
        if (x == "supplier") {
          setSelectedFilters({
            ...selectedFilters,
            [x]: [...filterOptions?.[x].map((option: any) => option.vendor)],
          });
        } else {
          setSelectedFilters({
            ...selectedFilters,
            [x]: [...filterOptions?.[x].map((option: any) => option?.[x])],
          });
        }
      }
    } else {
      if (x == "manager") {
        setSelectedFilters({
          ...selectedFilters,
          l3: [],
          [x]: [value],
        });
        selectedFilters[x] = [value];
      } else {
        setSelectedFilters({ ...selectedFilters, [x]: [...value] });
      }
    }
  };

  const handleClose = (filter: string) => {
    if (filter === "manager") {
      setSelectedFilters({ ...selectedFilters, l1: [], l2: [], l3: [] });
      getFilters({ ...selectedFilters, l1: [], l2: [], l3: [] }, "manager");

      fetchData();
    } else if (filter === "l1") {
      setSelectedFilters({ ...selectedFilters, l2: [], l3: [] });
      getFilters({ ...selectedFilters, l2: [], l3: [] }, "l1");
      fetchData();
    } else if (filter === "l2") {
      setSelectedFilters({ ...selectedFilters, l3: [] });
      getFilters({ ...selectedFilters, l3: [] }, "l2");
      fetchData();
    } else if (filter === "l3") {
      // setIsLoading(true);
      setSelectedFilters({ ...selectedFilters });
      fetchData();
    }
  };

  const handleClear = (filter: string) => {
    isCleared = true;
    if (filter === "manager") {
      setSelectedFilters({
        ...selectedFilters,
        manager: [],
        l1: [],
        l2: [],
        l3: [],
      });
      getFilters(
        { ...selectedFilters, manager: [], l1: [], l2: [], l3: [] },
        "manager"
      );
      fetchData("manager");
    } else if (filter === "l1") {
      setSelectedFilters({
        ...selectedFilters,
        l1: [],
        l2: [],
        l3: [],
      });
      getFilters({ ...selectedFilters, l1: [], l2: [], l3: [] }, "l1");
      fetchData("l1");
    } else if (filter === "l2") {
      setSelectedFilters({
        ...selectedFilters,
        l2: [],
        l3: [],
      });
      getFilters({ ...selectedFilters, l2: [], l3: [] }, "l2");
      fetchData("l2");
    } else if (filter === "l3") {
      setSelectedFilters({
        ...selectedFilters,
        l3: [],
      });
      getFilters({ ...selectedFilters, l3: [] }, "l3");
      fetchData("l3");
    }
  };

  const handleRowClick = (item: any) => {
    navigate(`/category-progress-tracker?manager=${encodeURIComponent(item)}`);
  };

  const handleCategoryClick = (item: TableRow, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(
      `/category-progress-tracker?manager=${encodeURIComponent(
        item.owner
      )}&category=${encodeURIComponent(item.category)}`
    );
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className="progresstrackcontainer w-full ">
      <MainHeader
        onVisibilityChange={setIsVisible}
        isRightVisible={isRightVisible}
        setIsRightVisible={setIsRightVisible}
        topContent={
          <div className="flex justify-between items-center w-full">
            <div className="text-left pageline">
              <span className="pagelinestart">Execution Tracking</span>{" "}
              <span className="pagelineslash">/</span>{" "}
              <span className="pagelinesub">Category Progress Tracker</span>
            </div>
          </div>
        }
        bottomContent={
          <>
            <div className="sub-heading-text font-bold flex flex-col items-start">
              <span className="filterHead textNormal">Category Manager</span>
              <SelectPicker
                data={
                  filterOptions["manager"]?.length
                    ? filterOptions["manager"]
                    : []
                }
                labelKey={"manager"}
                valueKey={"manager"}
                value={
                  selectedFilters["manager"]
                    ? selectedFilters["manager"][0]
                    : []
                }
                onClose={() =>
                  filtersMount.current ? handleClose("manager") : ""
                }
                onChange={(value: any) => {
                  handleFilterUpdate(value, "manager");
                }}
                onClean={() => {
                  handleClear("manager");
                }}
              />
            </div>
            {["l1", "l2", "l3"].map((filterEntity) => (
              <div
                className="sub-heading-text font-bold flex flex-col items-start"
                key={filterEntity}
              >
                <span className="filterHead">{filterEntity}</span>
                <CheckPicker
                  data={
                    filterOptions[filterEntity].length
                      ? filterOptions[filterEntity]
                      : []
                  }
                  placeholder="All"
                  labelKey={
                    filterEntity === "supplier"
                      ? "master_vendor_name"
                      : filterEntity
                  }
                  valueKey={
                    filterEntity === "supplier"
                      ? "master_vendor_name"
                      : filterEntity
                  }
                  placement={
                    filterEntity === "district" ? "bottomEnd" : "bottomStart"
                  }
                  value={
                    selectedFilters[filterEntity]
                      ? selectedFilters[filterEntity]
                      : []
                  }
                  onClose={() =>
                    filtersMount.current ? handleClose(filterEntity) : ""
                  }
                  onClean={() => handleClear(filterEntity)}
                  onChange={(value) => handleFilterUpdate(value, filterEntity)}
                  loading={filtersLoading}
                />
              </div>
            ))}
          </>
        }
      ></MainHeader>
      <MainContent isVisible={isVisible} fullHeight={true}>
        <div className="contentBox customtab h-[100%]">
          <div className="common-table-header-row">
            <div className="common-table-header">Progress Tracking Table</div>
          </div>
          <div className="common-table-ui" style={{ height: "66vh" }}>
            <table
              className="progressChecker !text-center"
              style={{ height: "100%" }}
            >
              <thead>
                <tr>
                  <th rowSpan={2}>Categories</th>
                  <th rowSpan={2}>Owner</th>
                  <th colSpan={2}>Benefits</th>
                  <th rowSpan={2}>Status</th>
                  <th colSpan={5}>Stage</th>
                </tr>
                <tr>
                  <th>Total Annualised Benefits</th>
                  <th>Captured Benefits</th>
                  <th>
                    Insights & <br />
                    Validation
                  </th>
                  <th>
                    Strategy
                    <br /> Development{" "}
                  </th>
                  <th>
                    Vendor <br /> Engagement
                  </th>
                  <th>Execution</th>
                  <th>
                    Value <br />
                    Capture
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((item) => (
                  <tr key={item.id}>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      {item.category}
                    </td>
                    <td style={{ cursor: "pointer" }}>
                      {item.owner.split(",").map((i: any, ind: any) => (
                        <>
                          {ind > 0 && <span>{`, `}</span>}
                          <span
                            className="hover:text-[#86bc25]"
                            onClick={() => handleRowClick(i)}
                          >
                            {i}
                          </span>
                        </>
                      ))}
                    </td>
                    <td>{numberFormatter(item.targetBenefits, 2, "$")}</td>
                    <td>{numberFormatter(item.capturedBenefits, 2, "$")}</td>

                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressSquare color={item.squareStatus} />
                    </td>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressCircle progress={item.categoryBaseline / 4} />
                    </td>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressCircle progress={item.categoryStrategy / 4} />
                    </td>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressCircle progress={item.vendorEngagement / 4} />
                    </td>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressCircle progress={item.implementation / 4} />
                    </td>
                    <td
                      onClick={(e) => handleCategoryClick(item, e)}
                      style={{ cursor: "pointer" }}
                    >
                      <ProgressCircle
                        progress={
                          item.valueCapture >= 3 ? 1 : item.valueCapture / 4
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td></td>
                  <td>{numberFormatter(tableTotalData?.totalOpp, 2, "$")}</td>
                  <td>{numberFormatter(tableTotalData?.totalBen, 2, "$")}</td>

                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </MainContent>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onManagerChange: (data: any) => dispatch({ type: MANAGER, data: data }),
});

export default connect(null, mapDispatchToProps)(ProgressTrackerCategory);
